import { computed, action, isArrayLike, observable } from 'mobx';
import { StoreBase } from '../../../common/StoreBase';
import { ITableConfiguration } from '../models';
import { ITableProps } from '@kurtosys/ksys-app-components/dist/components/base/Table/models/ITableProps';
import { IPerformanceRow } from '../models/ITablePerformanceRow';
import { ITableColumn } from '@kurtosys/ksys-app-components/dist/components/base/Table/models/ITableColumn';
import { IBreakpointProps } from '@kurtosys/ksys-app-components/dist/models/IBreakpointProps';
import { ITableComponentProps } from '../models/ITableComponentProps';
import { isNullOrEmpty } from '@kurtosys/ksys-app-template/dist/utils/typeChecks';
import { ITableHeaderGroup } from '@kurtosys/ksys-app-components/dist/components/base/Table/models/ITableHeaderGroup';
import { Table as UnstyledTable } from '@kurtosys/ksys-app-components/dist/components/base/Table/Table';
export class TableStore extends StoreBase {
	static componentKey: 'table' = 'table';

	@computed
	get configuration(): ITableConfiguration | undefined {
		if (this.storeContext && this.storeContext.appStore) {
			return this.storeContext.appStore.getComponentConfiguration(TableStore.componentKey);
		}
	}

	@computed
	get breakpoints(): IBreakpointProps<ITableConfiguration>[] | undefined {
		return this.configuration && this.configuration.breakpoints;
	}

	isBreakpointProps(props: ITableComponentProps | IBreakpointProps<ITableComponentProps>[] | undefined): props is IBreakpointProps<ITableComponentProps>[] {
		return !!(props && Array.isArray(props) && (props[0] as IBreakpointProps<ITableComponentProps>).modifier && (props[0] as IBreakpointProps<ITableComponentProps>).size);
	}

	isMultiTableDataSet(data: { [property: string]: IPerformanceRow[] }): data is { [property: string]: IPerformanceRow[] } {
		return typeof data === 'object' && Object.keys(data).every((key: string) => data[key].length > 0);
	}

	orchestrateProps(config: ITableConfiguration | undefined): ITableProps<IPerformanceRow>[] {
		let props: ITableProps<IPerformanceRow> = { queryStore: this.storeContext.queryStore, ...this.mergeQueriesAndProps(config) };
		let multiTableDataSet: ITableProps<IPerformanceRow>[] = [];

		// Get dynamic values
		if (config) {
			const { dynamicRows, dynamicColumns } = config;

			// First Sort out the columns, then it will be available if multi dataset
			if (dynamicColumns && dynamicColumns.query) {
				const { type = 'append', query } = dynamicColumns;
				const columns = this.getQueryValue(query) as ITableColumn[];
				if (type === 'append') {
					props.columns = [...(props.columns || []), ...columns];
				}
				if (type === 'replace') {
					props.columns = columns;
				}
			}

			if (dynamicRows) {
				const rows = this.getQueryValue(dynamicRows);
				if (isArrayLike(rows)) {
					props.rows = rows;
				}
				if (this.isMultiTableDataSet(rows)) {
					const keys = Object.keys(rows);
					for (const key of keys) {
						const newProps = { ...props };
						newProps.rows = rows[key];
						// Resolve the table header values based on config key
						const columns = newProps.columns;
						const headerGroups = newProps.headerGroups;
						if (columns && headerGroups) {
							const headerColumnConfig = columns.find(col => !!(col.headerGroupId));
							if (headerColumnConfig) {
								const headerGroupConfig: ITableHeaderGroup[] = headerGroups.map((group) => {
									if (group.id === headerColumnConfig.headerGroupId) {
										group = { ...group, header: key };
									}
									return group;
								});
								newProps.headerGroups = headerGroupConfig;
							}
						}
						multiTableDataSet.push({ ...newProps });
					}
				}
			}
			// the following logic is used to hide rows that don't have data, regardless if it comes from rows or dynamicRows config.
			const { appStore } = this.storeContext;
			if (appStore && appStore.appComponentConfiguration && appStore.appComponentConfiguration.noDataOptions) {
				const { hideRow = false } = appStore.appComponentConfiguration.noDataOptions;
				if (hideRow) {
					if (!isNullOrEmpty(multiTableDataSet)) {
						multiTableDataSet = multiTableDataSet.map((data) => {
							return this.noDataCheck(data);
						});
					}
					else {
						props = this.noDataCheck(props);
					}
				}
			}
			if (appStore && appStore.splitAppOptions) {
				const { mustLimitRows, limit, start, hideDisclaimer } = appStore.splitAppOptions;
				if (mustLimitRows) {
					if (!isNullOrEmpty(multiTableDataSet)) {
						multiTableDataSet = multiTableDataSet.map((data) => {
							data.rows = (data.rows || []).slice(start, start + limit);
							return data;
						});
					}
					else {
						props.rows = (props.rows || []).slice(start, start + limit);
					}
				}
				if (hideDisclaimer) {
					if (!isNullOrEmpty(multiTableDataSet)) {
						multiTableDataSet = multiTableDataSet.map((data) => {
							data.disclaimersProps = undefined;
							return data;
						});
					}
					else {
						props.disclaimersProps = undefined;
					}
				}
			}
		}

		return multiTableDataSet.length > 0 ? multiTableDataSet : [props];
	}

	noDataCheck(props: ITableProps<IPerformanceRow>) {
		const { rows, columns } = props;
		if (rows !== undefined && isArrayLike(rows) && columns !== undefined) {
			props.rows = rows.filter((row: IPerformanceRow) => {
				return !this.rowHasNoData(row, { columnConfig: columns });
			});
		}
		return props;
	}

	@observable.ref
	props: ITableComponentProps | IBreakpointProps<ITableComponentProps>[] | undefined;

	@computed
	get hasData(): boolean | undefined {
		let props = this.props;
		if (props) {
			if (this.isBreakpointProps(props)) {
				const { screenSize } = this.storeContext.appStore;
				let currentBreakpointProps: ITableComponentProps = {};

				for (const prop of props) {
					const responsiveEventHandler = this.storeContext.appStore.responsiveEventHandler;
					const windowWidth = responsiveEventHandler.getWidth();
					const propModifier = prop.modifier;
					const propSize = prop.size;

					switch (propModifier) {
						case 'up':
							const breakpointWidth = responsiveEventHandler.getBreakPointWidth(prop.size);
							if (windowWidth >= breakpointWidth) {
								currentBreakpointProps = prop.props;
							}
							break;
						case 'down':
							const nextBreakpointWidth = responsiveEventHandler.getNextBreakpointWidth(prop.size);
							if (windowWidth < nextBreakpointWidth) {
								currentBreakpointProps = prop.props;
							}
							break;
						case 'only':
						default:
							if (propSize === screenSize) {
								currentBreakpointProps = prop.props;
							}
							break;
					}
				}
				props = currentBreakpointProps;
			}

			if (isArrayLike(props)) {
				return (props as ITableProps<IPerformanceRow>[]).some((prop) => {
					return this.rowsHasData(prop.rows, prop.columns);
				});
			}
			return props && this.rowsHasData(props.rows, props.columns);
		}
		return false;
	}

	rowsHasData(rows: IPerformanceRow[] | undefined, columns: ITableColumn[] | undefined): boolean {
		if (Array.isArray(rows) && rows.length > 0) {
			const columnConfig = columns;
			if (columnConfig) {
				const hasNoData = rows.map((row) => {
					return this.rowHasNoData(row, { columnConfig });
				});
				return !hasNoData.every(row => row);
			}
		}
		return false;
	}

	rowHasNoData(row: IPerformanceRow, options: { columnConfig: ITableColumn[] }): boolean {
		return Object.keys(row).every((columnKey) => {
			const column = options.columnConfig.find(col => col.key === columnKey);
			return !column || column.excludeFromNoDataCheck || row[columnKey] === undefined || row[columnKey] === null;
		});
	}

	@action
	async initialize(): Promise<void> {
		let props;
		const breakpoints = this.breakpoints;
		if (breakpoints && this.isBreakpointProps(breakpoints)) {
			props = breakpoints.map((breakpoint) => {
				return { size: breakpoint.size, modifier: breakpoint.modifier, props: this.orchestrateProps(breakpoint.props) };
			});
		}
		else {
			props = this.orchestrateProps(this.configuration);
			if (props && isArrayLike(props) && props.length === 1) {
				const appKey = this.storeContext && this.storeContext.appStore && this.storeContext.appStore.appKey;
				UnstyledTable.loadCellSuperscripts(appKey, props[0].columns || [], props[0].rows || [], this.storeContext.queryStore);
			}
		}
		this.props = props;
	}
}
